<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import GoalIndexSubject from './GoalIndexSubject.vue'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import type { GoalIndexSubject as IndexSubject } from './goal_index_types'

const COLUMN_COUNT = 3
const ALL_SUBJECTS = 'all'

const { publishedSubjects } = storeToRefs(useContentProfileStore())
const analytics = getAnalyticsTracker()
const route = useRoute()

const expandedBySubject = ref<Record<string, boolean>>({})

const subjectColumns = computed<IndexSubject[][]>(() => {
  const subjects = publishedSubjects.value
  if (!subjects.length) {
    return []
  }
  const columnLength = Math.ceil(subjects.length / COLUMN_COUNT)
  const columnCount = Math.ceil(subjects.length / columnLength)
  return Array.from({ length: columnCount }, (_, column) =>
    subjects.slice(column * columnLength, (column + 1) * columnLength)
  )
})

function setAllExpanded(expanded: boolean) {
  expandedBySubject.value = Object.fromEntries(
    publishedSubjects.value.map((subject: IndexSubject) => [subject.subject, expanded])
  )
}

function toggleAll(expanded: boolean) {
  analytics.sendEvent(EVENT_NAMES.GOAL_INDEX.TOGGLED_SUBJECT, {
    lgi_subject: ALL_SUBJECTS,
    lgi_expanded: expanded
  })
  setAllExpanded(expanded)
}

function updateExpanded(subjectName: string, expanded: boolean) {
  expandedBySubject.value[subjectName] = expanded
}

const expandedStates = computed(() => Object.values(expandedBySubject.value))
const allExpanded = computed(
  () => expandedStates.value.length > 0 && expandedStates.value.every((expanded) => expanded)
)
const allCollapsed = computed(
  () => expandedStates.value.length > 0 && expandedStates.value.every((expanded) => !expanded)
)

watch(publishedSubjects, () => setAllExpanded(true), { immediate: true })

analytics.sendEvent(EVENT_NAMES.GOAL_INDEX.VIEWED_PAGE, { ref: route.query.ref })
</script>

<template>
  <div class="container min-h-screen">
    <header class="text-center">
      <h1 class="header-xl mt-8 font-regular text-gray-900">Learning Goal Index</h1>
      <p class="mx-auto mb-6 text-text-xl text-gray-800">
        Explore all learning goals, objectives, and progress monitoring resources by subject area.
      </p>
    </header>
    <div>
      <div class="mb-4 text-right text-text-sm text-gray-700">
        <button
          type="button"
          class="text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4 disabled:cursor-not-allowed disabled:opacity-65 disabled:hover:no-underline"
          :disabled="allCollapsed"
          @click="toggleAll(false)">
          Collapse all
        </button>
        <span class="mx-1" aria-hidden="true">|</span>
        <button
          type="button"
          class="text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4 disabled:cursor-not-allowed disabled:opacity-65 disabled:hover:no-underline"
          :disabled="allExpanded"
          @click="toggleAll(true)">
          Expand all
        </button>
      </div>
      <div class="grid gap-x-8 md:grid-cols-3">
        <div v-for="(column, index) in subjectColumns" :key="`subject-column-${index}`" data-testid="subject-column">
          <GoalIndexSubject
            v-for="subject in column"
            :key="subject.subject"
            :subject="subject"
            :expanded="expandedBySubject[subject.subject]"
            @update:expanded="updateExpanded"
          />
        </div>
      </div>
    </div>
  </div>
</template>
